/** Exec slice — the reference `exec()` (design-reference/PointFive OS Original.dc.html, script lines 406–812), ported verbatim:
 *  Trades screen (filter/sort/page/select/stats/menus), AI Signals, Copy Trading, order-desk symbol filter, order ticket, toast. */
import type { ChangeEvent } from 'react';
import type { Position as ServerPosition, ProviderRisk } from '@pointfive/contracts';
import { api } from '@/client/api';
import { instrumentScale, trailingStop } from './helpers';
import { INSTRUMENTS } from '@pointfive/contracts';
import type { Ctx } from './ctx';
import type { ModelState, OpenPosition } from './state';
import type { ViewModel } from '@/screens/generated/view-model';

type InputEvt = ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
type TrFilterKey = 'trSide' | 'trStrat' | 'trAcct';
type TrMenuId = 'side' | 'strat' | 'acct';
// Reference keys `ordSide`, `ordValid`, `ordInvalid` are never bound by the template (absent from the generated
// ViewModel) and are omitted here per the integrator's rule; `ordBuy`/`ordSell`/`ordBtnOp`/`ordHasErr` carry the same facts.

export function buildExec(c: Ctx) {
  const S = c.S;
  const menu = (id: string) => ({
    open: S.menu === id,
    toggle: () => c.set((s) => ({ menu: s.menu === id ? null : id })),
  });

  // ── Trades ───────────────────────────────────────────────
  const RANGE: Record<string, number> = {
    today: 0,
    'this week': 6,
    'this month': 30,
    'this year': 400,
    all: 9999,
  };
  const rangeOff = RANGE[S.tradeRange];
  const maxOff = rangeOff === undefined ? 9999 : rangeOff;
  const q = S.trQ.trim().toUpperCase();
  const all = c.trades().map((r) => ({
    id: r[0],
    off: r[1],
    acct: r[2],
    sym: r[3],
    side: r[4],
    lots: r[5],
    entry: r[6],
    exit: r[7],
    pnl: r[8],
    durS: r[9],
    strat: r[10],
    tagged: !!r[11],
    time: r[12],
  }));
  const filtered = all.filter(
    (t) =>
      t.off <= maxOff &&
      (!q || t.sym.indexOf(q) === 0) &&
      (S.trSide === 'all' || t.side.toLowerCase() === S.trSide) &&
      (S.trStrat === 'all' || t.strat === S.trStrat) &&
      (S.trAcct === 'all' || t.acct === S.trAcct),
  );
  const sorted = filtered
    .slice()
    .sort((a, b) => (S.trSort === 'pnl' ? (a.pnl - b.pnl) * -S.trDir : (a.off - b.off) * -S.trDir));
  const PAGE = 10;
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE));
  const page = Math.min(S.trPage, pages - 1);
  const view = sorted.slice(page * PAGE, page * PAGE + PAGE);
  const wins = filtered.filter((t) => t.pnl > 0),
    losses = filtered.filter((t) => t.pnl < 0);
  const net = filtered.reduce((a, t) => a + t.pnl, 0);
  const comm = filtered.reduce((a, t) => a + t.lots * 8, 0);
  const grossWin = wins.reduce((a, t) => a + t.pnl, 0),
    grossLoss = losses.reduce((a, t) => a + t.pnl, 0);
  const tagged = filtered.filter((t) => t.tagged).length;
  const selSet = S.trSel.filter((id) => filtered.some((t) => t.id === id));
  const allSel = filtered.length > 0 && selSet.length === filtered.length;
  const strats = c.data.exec.tradeStrategies;
  const accts = c.data.exec.tradeAccounts;
  const mkMenu = (id: TrMenuId, key: TrFilterKey, items: readonly string[], allLabel: string) => {
    const m = menu(id);
    const pick = (v: string) => {
      const patch: Partial<ModelState> = { menu: null, trPage: 0 };
      patch[key] = v;
      c.set(patch);
    };
    return {
      open: m.open,
      toggle: m.toggle,
      label: S[key] === 'all' ? allLabel : key === 'trSide' ? S[key].toUpperCase() : S[key],
      items: [{ label: allLabel, on: S[key] === 'all', go: () => pick('all') }].concat(
        items.map((v) => ({
          label: key === 'trSide' ? v.toUpperCase() : v,
          on: S[key] === v,
          go: () => pick(v),
        })),
      ),
    };
  };

  // ── Signals ──────────────────────────────────────────────
  const SIG = c.data.exec.signals;
  const sgAll = SIG.map(([sym, dir, conf, entry, sl, tp, rr, note]) => ({
    sym,
    dir,
    conf,
    entry,
    sl,
    tp,
    rr,
    note,
    confLabel: conf + '%',
    dirFg: dir === 'LONG' ? 'var(--pos)' : 'var(--neg)',
    dirBg:
      dir === 'LONG'
        ? 'color-mix(in srgb, var(--pos) 12%, transparent)'
        : 'color-mix(in srgb, var(--neg-fill) 14%, transparent)',
    lowConf: conf < 70,
    accept: () => {
      c.set({
        screen: 'tradehub',
        ordSide: dir === 'LONG' ? 'buy' : 'sell',
        ordSl: sl.replace(/,/g, ''),
        ordTp: tp.replace(/,/g, ''),
      });
      c.flash(sym + ' ' + dir + ' loaded into the ticket — review and confirm', 'pos');
    },
    dismiss: () => {
      c.set((s) => ({ sgDismissed: s.sgDismissed.concat(sym) }));
      c.flash(sym + ' signal dismissed', 'mut');
    },
  })).filter((s) => S.sgDismissed.indexOf(s.sym) < 0 && s.conf >= S.sgConf);

  // ── Copy trading ─────────────────────────────────────────
  const PROV = c.data.exec.providers;
  const riskTone: Record<ProviderRisk, string> = {
    Conservative: 'var(--pos)',
    Moderate: 'var(--amb-2)',
    Aggressive: 'var(--neg)',
  };
  const cpProviders = PROV.map(([id, name, style, ret, dd, followers, risk, wr, pf, strats]) => {
    const following = S.cpFollow.indexOf(id) >= 0;
    const allowed = strats.filter((s) => S.cpStrats.indexOf(s) >= 0);
    return {
      id,
      name,
      style,
      risk,
      stratChips: strats.map((s) => {
        const on = S.cpStrats.indexOf(s) >= 0;
        return {
          label: s,
          bd: on ? 'color-mix(in srgb, var(--pos) 32%, transparent)' : 'var(--l10)',
          fg: on ? 'var(--pos-2)' : 'var(--faint)',
        };
      }),
      allBlocked: allowed.length === 0,
      blockedNote:
        allowed.length === 0
          ? 'None of its strategies are enabled — nothing would copy'
          : allowed.length < strats.length
            ? strats.length - allowed.length + ' of its strategies are filtered out'
            : '',
      partlyBlocked: allowed.length > 0 && allowed.length < strats.length,
      ret: '+' + ret.toFixed(1) + '%',
      dd: '−' + dd.toFixed(1) + '%',
      followers: followers.toLocaleString('en-US'),
      wr: wr + '%',
      pf: pf.toFixed(2),
      riskFg: riskTone[risk],
      following,
      btnLabel: following ? 'Following' : 'Follow',
      btnBd: following ? 'var(--l10)' : 'color-mix(in srgb, var(--pos) 50%, transparent)',
      btnBg: following ? 'transparent' : 'color-mix(in srgb, var(--pos) 8%, transparent)',
      btnFg: following ? 'var(--muted)' : 'var(--pos)',
      go: () => {
        if (following) {
          c.set((s) => ({ cpFollow: s.cpFollow.filter((x) => x !== id) }));
          c.flash('Stopped copying ' + name, 'mut');
        } else c.set({ cpModal: id });
      },
    };
  });
  const cpModalProv = cpProviders.filter((p) => p.id === S.cpModal)[0] || null;
  const cpActive = cpProviders
    .filter((p) => p.following)
    .map((p) =>
      Object.assign({}, p, {
        alloc: c.fmtP(2500 * S.cpMult),
        live: '+$' + (184 * S.cpMult).toFixed(2),
      }),
    );

  // ── Order ticket ─────────────────────────────────────────
  const ch = c.chart();
  const lots = parseFloat(S.ordLots);

  // The desk works ONE instrument, and the chart, the ticket and the order all have to be about that one. The
  // reference hardcoded XAUUSD in each of the three and told the trader the rest "are not wired in this prototype";
  // a ticket priced off gold while the chart shows EURUSD is the kind of mismatch that ends in a real position.
  const deskSym = S.deskSym || 'XAUUSD';
  const quoted = c.data.exec.quotes.find((q) => q[0] === deskSym);
  const bid = deskSym === 'XAUUSD' || !quoted ? ch.bid : quoted[1];
  const ask = deskSym === 'XAUUSD' || !quoted ? ch.ask : quoted[2];
  const px = parseFloat(S.ordSide === 'buy' ? ask : bid);
  const isBuy = S.ordSide === 'buy';
  const CONTRACT = 100,
    EQUITY = 542692;
  // A ticked box drops a draft level a default distance from entry; the trader edits from there.
  // Default distances scale with the instrument: eight points is a sane gold stop and an absurd EURUSD one. Held as
  // a ratio against gold's own base so XAUUSD keeps the reference's exact 8 and 16, and every other instrument gets
  // the same shape of trade at its own scale. A 2:1 target either way.
  const scale = instrumentScale(deskSym);
  const decimals = scale.decimals;
  const step = 8 * (scale.base / 4036);
  const autoSl = (isBuy ? px - step : px + step).toFixed(decimals);
  const autoTp = (isBuy ? px + step * 2 : px - step * 2).toFixed(decimals);
  const slStr = S.ordSlOn ? (S.ordSl === '' ? autoSl : S.ordSl) : '';
  const tpStr = S.ordTpOn ? (S.ordTp === '' ? autoTp : S.ordTp) : '';
  const sl = parseFloat(slStr),
    tp = parseFloat(tpStr);
  let err = '';
  if (!(lots > 0)) err = 'Enter a lot size greater than 0.';
  else if (lots > 1) err = 'Lot size exceeds your 1.00 session cap.';
  else if (!S.ordSlOn) err = 'Your Risk Rules require a stop loss on every entry — tick Stop Loss.';
  else if (!(sl > 0)) err = 'Enter a stop loss price.';
  else if (isBuy && sl >= px) err = 'Stop loss must sit below entry on a buy.';
  else if (!isBuy && sl <= px) err = 'Stop loss must sit above entry on a sell.';
  else if (S.ordTpOn && !(tp > 0)) err = 'Enter a take profit price or untick Take Profit.';
  else if (S.ordTpOn && isBuy && tp <= px) err = 'Take profit must sit above entry on a buy.';
  else if (S.ordTpOn && !isBuy && tp >= px) err = 'Take profit must sit below entry on a sell.';
  // The open position on the instrument the chart is showing. One per symbol on this desk by construction — a
  // second entry on the same instrument scales the first rather than opening a rival position.
  const livePos = S.openPos.find((p) => p.sym === deskSym) ?? null;

  // A trailing stop moves, so the marker has to move with it — a static line under a label reading "TRAILING" is
  // the chart contradicting itself. Computed through the shared helper so the positions table below the chart shows
  // the same number.
  const trailing = Boolean(livePos && S.ruleOn['trail']);
  const liveSlPrice = (() => {
    if (!livePos || livePos.sl === '—') return '';
    const stop = parseFloat(livePos.sl.replace(/,/g, ''));
    if (!trailing) return String(stop);
    const trailed = trailingStop({
      entry: parseFloat(livePos.open.replace(/,/g, '')),
      stop,
      current: parseFloat(String(ch.lastPrice).replace(/,/g, '')),
      isLong: livePos.type.indexOf('BUY') === 0,
    });
    return trailed.toFixed(decimals);
  })();

  const slValid = sl > 0 && (isBuy ? sl < px : sl > px);
  const tpValid = S.ordTpOn && tp > 0 && (isBuy ? tp > px : tp < px);
  const sized = lots > 0 && lots <= 1;
  const riskUsd = sized && slValid ? Math.abs(px - sl) * lots * CONTRACT : 0;
  const rewardUsd = sized && tpValid ? Math.abs(tp - px) * lots * CONTRACT : 0;
  const rrTxt = riskUsd && rewardUsd ? (rewardUsd / riskUsd).toFixed(2) + ' : 1' : '—';
  const pctOf = (v: number) => {
    const p = (v / EQUITY) * 100;
    const s = p >= 1 ? p.toFixed(2) : p >= 0.1 ? p.toFixed(3) : p >= 0.001 ? p.toFixed(4) : null;
    return (s === null ? '<0.001' : s) + '% of equity';
  };
  const box = (on: boolean, tone: string) => ({
    bd: on ? 'color-mix(in srgb, ' + tone + ' 45%, transparent)' : 'var(--l8)',
    headBg: on ? 'color-mix(in srgb, ' + tone + ' 16%, transparent)' : 'transparent',
    headFg: on ? tone : 'var(--muted)',
    boxBd: on ? tone : 'var(--l10)',
    boxBg: on ? tone : 'transparent',
    boxIcon: on ? 'ph-check' : 'ph-empty',
  });
  const slBox = box(S.ordSlOn, 'var(--neg)'),
    tpBox = box(S.ordTpOn, 'var(--pos)');
  const submit = () => {
    if (err) {
      c.set({ ordErr: err });
      c.flash(err, 'neg');
      return;
    }
    // The draft is SNAPSHOTTED here (mandate §5): the modal renders it, confirmOrder submits it, the toast echoes
    // it. Retargeting the desk while the modal is open cannot change what is about to be traded — and the modal
    // can never show a previous instrument's name over this one's price, which is exactly the bug this replaces.
    c.set({
      ordModal: true,
      ordErr: '',
      ordDraft: {
        sym: deskSym,
        side: S.ordSide === 'buy' ? ('buy' as const) : ('sell' as const),
        lots,
        px,
        sl: S.ordSlOn ? Number(slStr) || null : null,
        tp: S.ordTpOn ? Number(tpStr) || null : null,
        // The draft names the account the ORDER will carry: a picked firm, or the session default — which the
        // server resolves, so the modal never claims a firm name the client cannot verify (ADR-016).
        account:
          (S.thAcct ? c.data.core.accounts.find((a) => a.id === S.thAcct)?.firm : null) ?? 'Session default',
      },
    });
  };
  /** Confirming an order goes through the §13.4 funnel, and the position appears only when the funnel says it exists.
   *
   *  There is no optimistic row here, deliberately. Showing a position the moment the button is pressed is the exact
   *  lie the funnel is built to prevent: a trader would see a holding that the broker may have refused, or — worse —
   *  may not have answered about at all. The three outcomes are three different things to say:
   *
   *  - `done` — the position is real, and it is drawn from what the server recorded rather than from what was typed.
   *  - `failed` — the broker or a rule refused it, and the trader is told which and why.
   *  - `unconfirmed` — nobody knows yet. The order may be live. The screen says so and offers *no* retry, because
   *    retrying is the one action that can double a position (REQ-LADDER); the reconciler resolves it instead. */
  /** AUD-001's hardening: the RESPONSE and the TRUTH are different things, and losing the first must not lose
   *  the second. The dispatch is raced against a bounded silence; silence or a transport-shaped failure routes to
   *  `execute.orders.intent` — the confirm-by-read the server has always offered — under the SAME key, which can
   *  say `done`/`rejected`/`unconfirmed`/absent without ever re-dispatching the order. A typed refusal (the
   *  trader's own rules speaking) is terminal and is never second-guessed by a read. */
  /** Server positions → the desk's rows. THE ONLY WAY a position row is built (SM: authoritative read-back):
   *  every mutation path sets `openPos` from the server's own `positions` array, so the table can never hold an
   *  optimistic row the funnel refused or a price the broker did not report. */
  const positionRows = (positions: readonly ServerPosition[]): OpenPosition[] =>
    positions
      .filter((p) => p.status === 'open')
      .map((p) => {
        const d = instrumentScale(p.symbol).decimals;
        return {
          id: p.id,
          accountId: p.accountId,
          sym: p.symbol,
          type: p.side,
          vol: p.volume.toFixed(2),
          open: p.openPrice.toFixed(d),
          cur: p.currentPrice.toFixed(d),
          sl: p.sl === null ? '—' : p.sl.toFixed(d),
          tp: p.tp === null ? '—' : p.tp.toFixed(d),
          pnl: c.fmt(p.pnl),
          pnlFg: p.pnl > 0 ? 'var(--pos)' : p.pnl < 0 ? 'var(--neg)' : 'var(--text2)',
        };
      });

  const settleFromRead = async (key: string): Promise<void> => {
    try {
      const read = await api.execute.orders.intent({ idempotencyKey: key });
      const status = read.intent?.status ?? null;
      if (status === 'done') {
        // The position rows come from the server's record, not from what was typed: the read answers with the
        // authoritative open positions and the desk redraws from them.
        c.set({ ordSubmitting: false, openPos: positionRows(read.positions) });
        c.flash('The order went through — your reply was lost, not your order.', 'pos');
        return;
      }
      if (status === 'failed') {
        c.set({ ordSubmitting: false });
        c.flash(read.intent?.reason ?? 'The order was not accepted.', 'neg');
        return;
      }
      if (status !== null) {
        // claimed / dispatched / unconfirmed: nobody knows yet, and the reconciler owns finding out.
        c.set({ ordSubmitting: false, ordUnconfirmed: key });
        c.flash('The broker did not answer. We are checking whether the order went through.', 'mut');
        return;
      }
      // Nothing recorded under the key: the request never reached the funnel. The one case a retry is safe.
      c.set({ ordSubmitting: false });
      c.flash('The order did not go through — nothing was recorded. It is safe to try again.', 'neg');
    } catch {
      // Even the read failed: the network is genuinely down. Say so; never guess an outcome.
      c.set({ ordSubmitting: false, ordUnconfirmed: key });
      c.flash(
        'We cannot reach the server. The order state will be confirmed when the connection returns.',
        'mut',
      );
    }
  };

  const confirmOrder = async () => {
    const draft = S.ordDraft;
    if (!draft) return; // no snapshot, no order — the modal cannot confirm what was never drafted
    const key = `ord-${draft.side}-${draft.lots.toFixed(2)}-${Date.now()}`;
    c.set({ ordModal: false, ordSubmitting: true, ordDraft: null });
    const SILENCE_MS = 15_000;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const result = await Promise.race([
        api.execute.orders.market({
          idempotencyKey: key,
          // The account dimension rides the wire when the trader chose one; omitted, the SESSION's own account
          // answers (ADR-016). Stale-account submission is impossible: the id is read here, at send time.
          accountId: S.thAcct,
          symbol: draft.sym,
          side: draft.side === 'buy' ? 'BUY' : 'SELL',
          volume: draft.lots,
          sl: draft.sl,
          tp: draft.tp,
          strategyTag: null,
        }),
        new Promise<never>((_resolve, reject) => {
          timer = setTimeout(() => reject(new Error('__silence__')), SILENCE_MS);
        }),
      ]);
      clearTimeout(timer);

      if (result.intent.status === 'done' && result.order) {
        // The table state is the server's `positions` array, never a hand-built row: the id rides along (close
        // and modify address it), and the open price is what the broker REPORTED, not what the ticket hoped.
        c.set({ openPos: positionRows(result.positions), ordSubmitting: false });
        c.flash(
          (result.order.side === 'BUY' ? 'Bought ' : 'Sold ') +
            result.order.volume.toFixed(2) +
            ' ' +
            result.order.symbol +
            ' at ' +
            (result.order.price === null ? px.toFixed(2) : result.order.price.toFixed(2)),
          'pos',
        );
        return;
      }

      if (result.intent.status === 'unconfirmed') {
        c.set({ ordSubmitting: false, ordUnconfirmed: key });
        c.flash('The broker did not answer. We are checking whether the order went through.', 'mut');
        return;
      }

      c.set({ ordSubmitting: false });
      c.flash(result.intent.reason ?? 'The order was not accepted.', 'neg');
    } catch (error) {
      clearTimeout(timer);
      // Bounded silence, a dropped connection, or a server that answered garbage: ask what was RECORDED rather
      // than guessing from what was received (AUD-001). Never a re-dispatch — the read is the whole point.
      const code = (error as { code?: string }).code;
      const transportShaped =
        (error instanceof Error && error.message === '__silence__') ||
        code === 'UPSTREAM_DEGRADED' ||
        code === 'INTERNAL';
      if (transportShaped) {
        await settleFromRead(key);
        return;
      }
      // A refusal by the trader's own rules arrives here as a typed error, and it is theirs to read: the rule's own
      // wording, not a generic failure.
      c.set({ ordSubmitting: false });
      c.flash(error instanceof Error ? error.message : 'The order could not be placed.', 'neg');
    }
  };

  /* ── position management (implementation mandate §2) ────────────────────────────────────────────────────────
   *  Every action: one idempotency key, busy-guarded against the double click, terminal state from the SERVER'S
   *  positions array (never an optimistic splice), a transport-shaped failure settling through the same
   *  confirm-by-read the entry path uses, and a typed refusal shown in the rule's own words. */

  const runPositionOp = async (
    busyKey: string,
    key: string,
    call: () => Promise<{
      intent: { status: string; reason: string | null };
      positions: readonly ServerPosition[];
    }>,
    done: (positions: readonly ServerPosition[]) => void,
  ): Promise<void> => {
    if (S.posBusy) return; // a second click on a money action is a second order — refused at the door
    c.set({ posBusy: busyKey });
    try {
      const result = await call();
      if (result.intent.status === 'done') {
        c.set({ posBusy: null, openPos: positionRows(result.positions) });
        done(result.positions);
        return;
      }
      if (result.intent.status === 'unconfirmed') {
        c.set({ posBusy: null, ordUnconfirmed: key });
        c.flash('The broker did not answer. We are checking whether it went through.', 'mut');
        return;
      }
      c.set({ posBusy: null });
      c.flash(result.intent.reason ?? 'The request was not accepted.', 'neg');
    } catch (error) {
      const code = (error as { code?: string }).code;
      if (code === 'UPSTREAM_DEGRADED' || code === 'INTERNAL') {
        // Ask what was RECORDED rather than guessing from what was received — the AUD-001 rule, applied here.
        try {
          const read = await api.execute.orders.intent({ idempotencyKey: key });
          c.set({ posBusy: null, openPos: positionRows(read.positions) });
          c.flash(
            read.intent?.status === 'done'
              ? 'The reply was lost, not the action — the table shows the recorded state.'
              : 'We could not confirm the action. The table shows the recorded state.',
            'mut',
          );
        } catch {
          c.set({ posBusy: null });
          c.flash('We cannot reach the server. Nothing was assumed.', 'neg');
        }
        return;
      }
      c.set({ posBusy: null });
      c.flash(error instanceof Error ? error.message : 'The request could not be made.', 'neg');
    }
  };

  const posCloseGo = async () => {
    const target = S.posCloseConfirm;
    if (!target) return;
    c.set({ posCloseConfirm: null });
    const key = `close-${target.id}-${Date.now()}`;
    await runPositionOp(
      `close-${target.id}`,
      key,
      () => api.execute.orders.close({ idempotencyKey: key, positionId: target.id }),
      () => c.flash(`${target.sym} ${target.vol} closed.`, 'pos'),
    );
  };

  const posCloseAllAsk = () => {
    if (S.openPos.length === 0) {
      c.flash('No open positions to close.', 'mut');
      return;
    }
    c.set({ posCloseAllConfirm: true });
  };

  /* ── Lock Day (mandate §10, SDD §8.1): manual lockout, optional close-all, confirm required ──────────── */
  const lockDayGo = async () => {
    const draft = S.lockDay;
    if (!draft || draft.busy) return;
    c.set({ lockDay: { ...draft, busy: true } });
    try {
      if (draft.closeAll && S.openPos.length > 0) {
        // Close-all rides the EXISTING money path — its own idempotency key, its own read-back. Order matters
        // only for legibility (reducing orders pass the funnel even under a lockout, by design): closing first
        // keeps the audit trail reading the way the trader meant it — flatten, then lock.
        const key = `lockday-closeall-${Date.now()}`;
        const read = await api.execute.orders.closeAll({ idempotencyKey: key, accountId: null });
        c.set({ openPos: positionRows(read.positions) });
      }
      const lock = await api.me.lockouts.lockDay({ idempotencyKey: `lockday-${Date.now()}` });
      const until = lock.expiresAt.slice(11, 16);
      c.set({ lockDay: null, dayLockedUntil: lock.expiresAt });
      c.flash(
        lock.resumed
          ? `The day was already locked — trading resumes after ${until} UTC.`
          : `Day locked. Order entry refuses until ${until} UTC; the calendar unlocks it, not a button.`,
        'mut',
      );
    } catch (error) {
      c.set({ lockDay: draft ? { ...draft, busy: false } : null });
      c.flash(error instanceof Error ? error.message : 'The day could not be locked.', 'neg');
    }
  };

  const posCloseAllGo = async () => {
    c.set({ posCloseAllConfirm: false });
    const before = S.openPos.length;
    const key = `closeall-${Date.now()}`;
    await runPositionOp(
      'closeAll',
      key,
      () => api.execute.orders.closeAll({ idempotencyKey: key, accountId: null }),
      (positions) => {
        const left = positions.filter((p) => p.status === 'open').length;
        // Partial failure is NAMED, never averaged away: the trader learns exactly what still stands.
        c.flash(
          left === 0
            ? `All ${before} position${before === 1 ? '' : 's'} closed.`
            : `${before - left} closed; ${left} could not be closed and remain${left === 1 ? 's' : ''} open.`,
          left === 0 ? 'pos' : 'mut',
        );
      },
    );
  };

  const posModifyGo = async () => {
    const draft = S.posModify;
    if (!draft) return;
    const parse = (v: string): number | null | undefined => {
      const t = v.trim();
      if (t === '') return null; // an emptied field REMOVES the level, deliberately
      const n = Number(t);
      return Number.isFinite(n) && n > 0 ? n : undefined;
    };
    const sl = parse(draft.sl);
    const tp = parse(draft.tp);
    if (sl === undefined || tp === undefined) {
      c.flash('Stop loss and take profit must be prices, or empty to remove the level.', 'neg');
      return;
    }
    c.set({ posModify: null });
    const key = `modify-${draft.id}-${Date.now()}`;
    await runPositionOp(
      `modify-${draft.id}`,
      key,
      () => api.execute.orders.modify({ idempotencyKey: key, positionId: draft.id, sl, tp }),
      () => c.flash(`${draft.sym} levels updated.`, 'pos'),
    );
  };

  // ── Order desk symbol filter ─────────────────────────────
  const QUOTES: readonly (readonly [string, string, string, string])[] = [
    ['XAUUSD', ch.bid, ch.ask, ch.spread],
    ...c.data.exec.quotes,
    // Desk additions (mandate §9). In today's sim deployment every instrument the contracts carry is already on
    // the desk, so this stays empty — but the control validates against the CONTRACT, not the current list, so it
    // starts working the day the instrument set outgrows the default desk.
    ...S.deskExtra
      .filter((sym) => sym !== 'XAUUSD' && !c.data.exec.quotes.some((q) => q[0] === sym))
      .map((sym) => [sym, '—', '—', '—'] as const),
  ];

  /* ── add symbol (mandate §9): validated, honest in both refusal directions ── */
  const deskAddGo = () => {
    const raw = S.deskAddInput.trim().toUpperCase();
    if (!raw) return;
    if (QUOTES.some((q) => q[0] === raw)) {
      c.flash(`${raw} is already on the desk list.`, 'mut');
      c.set({ deskAddOpen: false, deskAddInput: '' });
      return;
    }
    if (!INSTRUMENTS[raw]) {
      c.flash(`${raw} is not in this deployment's instrument set — nothing could quote it.`, 'neg');
      return;
    }
    c.set((st) => ({ deskExtra: [...st.deskExtra, raw], deskAddOpen: false, deskAddInput: '' }));
    c.flash(`${raw} added to the desk list.`, 'pos');
  };
  const dq = (S.deskQ || '').trim().toUpperCase();
  const deskList = QUOTES.filter(
    (r) => (!dq || r[0].indexOf(dq) >= 0) && (!S.deskFavOnly || S.deskFav.indexOf(r[0]) >= 0),
  );

  const CONF_CHIPS: readonly (readonly [string, number])[] = [
    ['All', 0],
    ['≥ 70%', 70],
    ['≥ 80%', 80],
  ];
  const CP_TABS: readonly (readonly [string, string])[] = [
    ['providers', 'Providers'],
    ['active', 'Following'],
    ['settings', 'Settings'],
  ];

  return {
    // trades
    trRows: view.map((t) => ({
      id: t.id,
      date: c.dayLabel(t.off),
      acct: t.acct,
      sym: t.sym,
      side: t.side,
      lots: t.lots.toFixed(2),
      entry: t.entry,
      exit: t.exit,
      pnl: c.fmt(t.pnl),
      pnlFg: t.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
      dur: c.dur(t.durS),
      strat: t.strat,
      tagLabel: t.tagged ? 'Tagged' : 'Add',
      tagFg: t.tagged ? 'var(--pos)' : 'var(--faint)',
      tagBd: t.tagged ? 'color-mix(in srgb, var(--pos) 35%, transparent)' : 'var(--l10)',
      sideFg: t.side === 'BUY' ? 'var(--pos)' : 'var(--neg)',
      sideBg:
        t.side === 'BUY'
          ? 'color-mix(in srgb, var(--pos) 12%, transparent)'
          : 'color-mix(in srgb, var(--neg-fill) 14%, transparent)',
      sel: selSet.indexOf(t.id) >= 0,
      rowBg: selSet.indexOf(t.id) >= 0 ? 'var(--l6)' : 'transparent',
      toggle: () =>
        c.set((s) => ({
          trSel: s.trSel.indexOf(t.id) >= 0 ? s.trSel.filter((x) => x !== t.id) : s.trSel.concat(t.id),
        })),
    })),
    trHasRows: view.length > 0,
    trNoRows: view.length === 0,
    trEmptyMsg: all.length === 0 ? 'No trades recorded yet' : 'No trades match these filters',
    trCount: filtered.length === 1 ? '1 trade' : filtered.length + ' trades',
    trPager:
      filtered.length === 0
        ? 'Nothing to show'
        : 'Showing ' + (page * PAGE + 1) + '–' + (page * PAGE + view.length) + ' of ' + filtered.length,
    trPageLabel: 'Page ' + (page + 1) + ' of ' + pages,
    trPrev: () => c.set({ trPage: Math.max(0, page - 1) }),
    trNext: () => c.set({ trPage: Math.min(pages - 1, page + 1) }),
    trPrevDis: page === 0,
    trNextDis: page >= pages - 1,
    trPrevOp: page === 0 ? '0.4' : '1',
    trNextOp: page >= pages - 1 ? '0.4' : '1',
    trQ: S.trQ,
    setTrQ: (e: InputEvt) => c.set({ trQ: e.target.value, trPage: 0 }),
    trSideMenu: mkMenu('side', 'trSide', ['buy', 'sell'], 'All sides'),
    trStratMenu: mkMenu('strat', 'trStrat', strats, 'All strategies'),
    trAcctMenu: mkMenu('acct', 'trAcct', accts, 'All accounts'),
    trSortDate: () => c.set((s) => ({ trSort: 'date', trDir: s.trSort === 'date' ? -s.trDir : -1 })),
    trSortPnl: () => c.set((s) => ({ trSort: 'pnl', trDir: s.trSort === 'pnl' ? -s.trDir : -1 })),
    trDateArrow: S.trSort === 'date' ? (S.trDir === -1 ? '↓' : '↑') : '',
    trPnlArrow: S.trSort === 'pnl' ? (S.trDir === -1 ? '↓' : '↑') : '',
    trSelAll: allSel,
    trSelAllToggle: () => c.set({ trSel: allSel ? [] : filtered.map((t) => t.id) }),
    trSelCount: selSet.length === 1 ? '1 trade selected' : selSet.length + ' trades selected',
    trHasSel: selSet.length > 0,
    trClearSel: () => c.set({ trSel: [] }),
    trTagSel: () =>
      c.flash(
        selSet.length + ' trade' + (selSet.length === 1 ? '' : 's') + ' tagged to London Open Reversal',
        'pos',
      ),
    trExport: () => c.flash('Exported ' + filtered.length + ' trades to CSV', 'pos'),
    trRefresh: () => c.flash('Trades resynced from MetaAPI', 'pos'),
    trNet: c.fmt(net),
    trNetFg: net >= 0 ? 'var(--pos)' : 'var(--neg)',
    trGross: c.fmt(net + comm),
    trComm: '−' + c.fmtP(comm),
    trWr: filtered.length ? ((wins.length / filtered.length) * 100).toFixed(1) + '%' : '—',
    trAvgWin: wins.length ? c.fmt(grossWin / wins.length) : '—',
    trAvgLoss: losses.length ? c.fmt(grossLoss / losses.length) : '—',
    trAvgLossFg: losses.length ? 'var(--neg)' : 'var(--muted)',
    trTagged: tagged + '/' + filtered.length,
    trTaggedFg:
      filtered.length && tagged === filtered.length
        ? 'var(--pos)'
        : tagged === 0
          ? 'var(--neg)'
          : 'var(--amb-2)',

    // signals
    sgList: sgAll,
    sgHas: sgAll.length > 0,
    sgNone: sgAll.length === 0,
    sgScanning: S.sgScan === 'scanning',
    sgScanLabel: S.sgScan === 'scanning' ? 'Scanning…' : 'Scan watchlist',
    sgScan: () => {
      if (S.sgScan === 'scanning') return;
      c.set({ sgScan: 'scanning' });
      c.timers.after(1100, () => {
        c.set({ sgScan: 'done', sgDismissed: [] });
        c.flash('Watchlist scanned — 3 setups found', 'pos');
      });
    },
    sgConfChips: CONF_CHIPS.map(([label, v]) => ({
      label,
      on: S.sgConf === v,
      go: () => c.set({ sgConf: v }),
      bd: S.sgConf === v ? 'color-mix(in srgb, var(--acc-fill) 50%, transparent)' : 'var(--l10)',
      bg: S.sgConf === v ? 'var(--l6)' : 'transparent',
      fg: S.sgConf === v ? 'var(--text)' : 'var(--muted)',
    })),
    sgRestore: () => {
      c.set({ sgDismissed: [], sgConf: 0 });
      c.flash('Dismissed signals restored', 'pos');
    },
    wlInput: S.wlInput,
    wlErr: S.wlErr,
    wlHasErr: !!S.wlErr,
    wlCount: '(' + S.wl.length + '/8)',
    setWlInput: (e: InputEvt) => c.set({ wlInput: e.target.value, wlErr: '' }),
    // §4: the watchlist is SERVER state (prepare.watchlist.*) — the add validates locally, writes through the
    // procedure, and S.wl reconciles from the response's items, never from an optimistic concat.
    wlAdd: () => {
      const v = (S.wlInput || '').trim().toUpperCase();
      if (!v) return c.set({ wlErr: 'Enter a symbol first.' });
      if (!/^[A-Z0-9]{3,8}$/.test(v)) return c.set({ wlErr: 'Symbols are 3–8 letters or digits.' });
      if (S.wl.indexOf(v) >= 0) return c.set({ wlErr: v + ' is already on the watchlist.' });
      if (S.wl.length >= 8) return c.set({ wlErr: 'The watchlist holds 8 symbols — remove one first.' });
      void api.prepare.watchlist
        .add({ symbol: v })
        .then((out) => {
          c.set({ wl: out.items.map((i) => i.symbol), wlInput: '', wlErr: '' });
          c.flash(v + ' added to the watchlist', 'pos');
        })
        .catch((error: unknown) =>
          c.set({ wlErr: error instanceof Error ? error.message : 'The symbol could not be added.' }),
        );
    },
    wlHydrate: () => {
      if (S.wlLoaded) return;
      void api.prepare.watchlist
        .list()
        .then((out) => c.set({ wl: out.items.map((i) => i.symbol), wlLoaded: true }))
        .catch(() => c.set({ wlLoaded: true })); // the skeleton stands; adds/removes still write through
    },

    // copy trading
    cpConnected: S.cpConnected,
    cpDisconnected: !S.cpConnected,
    cpConnect: () => {
      c.set({ cpConnected: true });
      c.flash('CopyFactory connected to Exness #184745', 'pos');
    },
    cpDisconnect: () => {
      c.set({ cpConnected: false, cpFollow: [], cpTab: 'providers' });
      c.flash('CopyFactory disconnected — copying stopped', 'mut');
    },
    cpTabs: CP_TABS.map(([id, label]) => ({
      label,
      go: () => c.set({ cpTab: id }),
      bg: S.cpTab === id ? 'var(--rail)' : 'transparent',
      fg: S.cpTab === id ? 'var(--rail-ink)' : 'var(--muted)',
      fw: S.cpTab === id ? '600' : '500',
      count: id === 'active' ? String(S.cpFollow.length) : '',
    })),
    cpIsProviders: S.cpTab === 'providers',
    cpIsActive: S.cpTab === 'active',
    cpIsSettings: S.cpTab === 'settings',
    cpProviders,
    cpActive,
    cpHasActive: cpActive.length > 0,
    cpNoActive: cpActive.length === 0,
    cpModalOpen: !!cpModalProv,
    cpModalName: cpModalProv ? cpModalProv.name : '',
    cpModalRisk: cpModalProv ? cpModalProv.risk : '',
    cpModalDd: cpModalProv ? cpModalProv.dd : '',
    cpModalAlloc: c.fmtP(2500 * S.cpMult),
    cpClose: () => c.set({ cpModal: null }),
    cpConfirm: () => {
      const id = S.cpModal;
      if (id === null) return; // the confirm button only renders while a provider modal is open
      const name = (PROV.filter((p) => p[0] === id)[0] || [])[1];
      c.set((s) => ({ cpFollow: s.cpFollow.concat(id), cpModal: null, cpTab: 'active' }));
      c.flash('Now copying ' + name + ' at ' + S.cpMult.toFixed(1) + '× risk', 'pos');
    },
    cpStratFilters: c.data.exec.copyStrategies.map((s) => {
      const on = S.cpStrats.indexOf(s) >= 0;
      return {
        label: s,
        on,
        icon: on ? 'ph-check-circle' : 'ph-circle',
        iconFg: on ? 'var(--pos)' : 'var(--ghost)',
        fg: on ? 'var(--text)' : 'var(--muted2)',
        status: on ? 'Copying' : 'Ignored',
        statusFg: on ? 'var(--pos)' : 'var(--faint)',
        go: () =>
          c.set((s2) => ({ cpStrats: on ? s2.cpStrats.filter((x) => x !== s) : s2.cpStrats.concat(s) })),
      };
    }),
    cpStratCount: S.cpStrats.length + ' of 8 enabled',
    cpStratWarn: S.cpStrats.length === 0,
    cpMult: S.cpMult,
    cpMultLabel: S.cpMult.toFixed(1) + '×',
    setCpMult: (e: InputEvt) => c.set({ cpMult: +e.target.value }),
    cpMaxCopies: String(S.cpMaxCopies),
    setCpMaxCopies: (e: InputEvt) => c.set({ cpMaxCopies: Math.max(1, Math.min(10, +e.target.value || 1)) }),
    cpSlTpOn: S.cpCopySlTp,
    cpSlTpToggle: () => c.set((s) => ({ cpCopySlTp: !s.cpCopySlTp })),
    cpSlTpTrack: S.cpCopySlTp ? 'color-mix(in srgb, var(--pos) 55%, transparent)' : 'var(--l10)',
    cpSlTpKnob: S.cpCopySlTp ? 'translateX(14px)' : 'translateX(0)',
    cpSlTpKnobBg: S.cpCopySlTp ? 'var(--pos)' : 'var(--muted2)',

    // order desk symbol filter
    deskQ: S.deskQ,
    setDeskQ: (e: InputEvt) => c.set({ deskQ: e.target.value }),
    deskCount: deskList.length + ' of ' + QUOTES.length,
    deskEmpty: deskList.length === 0,
    deskEmptyMsg:
      S.deskFavOnly && !S.deskFav.length
        ? 'No favourites starred yet'
        : 'No symbol matches "' + (S.deskQ || '').trim() + '"',
    deskFavOnly: {
      on: S.deskFavOnly,
      go: () => c.set((s) => ({ deskFavOnly: !s.deskFavOnly })),
      title: S.deskFavOnly ? 'Show all symbols' : 'Show favourites only',
      icon: S.deskFavOnly ? 'ph-fill ph-star' : 'ph-star',
      fw: S.deskFavOnly ? '600' : '500',
      bd: S.deskFavOnly ? 'color-mix(in srgb, var(--amb) 50%, transparent)' : 'var(--l10)',
      bg: S.deskFavOnly ? 'color-mix(in srgb, var(--amb) 14%, transparent)' : 'transparent',
      fg: S.deskFavOnly ? 'var(--amb-2)' : 'var(--muted)',
    },
    deskSyms: deskList.map(([sym, bid, ask, spread]) => {
      const fav = S.deskFav.indexOf(sym) >= 0,
        active = sym === deskSym;
      return {
        sym,
        bid,
        ask,
        spread,
        fav,
        fw: active ? '600' : '500',
        rowBg: active ? 'var(--l4)' : 'transparent',
        starIcon: fav ? 'ph-fill ph-star' : 'ph-star',
        starFg: fav ? 'var(--amb)' : 'var(--ghost)',
        starLabel: (fav ? 'Remove ' : 'Add ') + sym + (fav ? ' from' : ' to') + ' favourites',
        star: () =>
          c.set((s) => ({
            deskFav: s.deskFav.indexOf(sym) >= 0 ? s.deskFav.filter((x) => x !== sym) : s.deskFav.concat(sym),
          })),
        // Selecting an instrument moves the chart, the ticket and the order together. The draft levels are cleared
        // with it: a stop of 4031.77 carried from gold onto EURUSD is not a stop, it is a number in the wrong box.
        pick: () => {
          if (active) {
            c.flash(sym + ' is already loaded', 'mut');
            return;
          }
          c.set({ deskSym: sym, ordSl: '', ordTp: '' });
          c.flash(sym + ' loaded on the chart and the ticket', 'mut');
        },
      };
    }),

    // ticket
    ordBuy: S.ordSide === 'buy',
    ordSell: S.ordSide === 'sell',
    setBuy: () => c.set({ ordSide: 'buy', ordErr: '' }),
    setSell: () => c.set({ ordSide: 'sell', ordErr: '' }),
    ordLots: S.ordLots,
    setOrdLots: (e: InputEvt) => c.set({ ordLots: e.target.value, ordErr: '' }),
    lotsUp: () =>
      c.set((s) => ({ ordLots: Math.min(1, (parseFloat(s.ordLots) || 0) + 0.01).toFixed(2), ordErr: '' })),
    lotsDown: () =>
      c.set((s) => ({
        ordLots: Math.max(0.01, (parseFloat(s.ordLots) || 0.02) - 0.01).toFixed(2),
        ordErr: '',
      })),
    ordNotional: sized ? c.fmtP(lots * CONTRACT * px) : '—',
    ordSl: slStr,
    setOrdSl: (e: InputEvt) => c.set({ ordSl: e.target.value, ordErr: '' }),
    ordTp: tpStr,
    setOrdTp: (e: InputEvt) => c.set({ ordTp: e.target.value, ordErr: '' }),
    ordSlOn: S.ordSlOn,
    ordTpOn: S.ordTpOn,
    slToggle: () => c.set((s) => ({ ordSlOn: !s.ordSlOn, ordSl: '', ordErr: '' })),
    tpToggle: () => c.set((s) => ({ ordTpOn: !s.ordTpOn, ordTp: '', ordErr: '' })),
    slToggleLabel: (S.ordSlOn ? 'Remove' : 'Add') + ' stop loss draft',
    tpToggleLabel: (S.ordTpOn ? 'Remove' : 'Add') + ' take profit draft',
    slBd: slBox.bd,
    slHeadBg: slBox.headBg,
    slHeadFg: slBox.headFg,
    slBoxBd: slBox.boxBd,
    slBoxBg: slBox.boxBg,
    slBoxIcon: slBox.boxIcon,
    tpBd: tpBox.bd,
    tpHeadBg: tpBox.headBg,
    tpHeadFg: tpBox.headFg,
    tpBoxBd: tpBox.boxBd,
    tpBoxBg: tpBox.boxBg,
    tpBoxIcon: tpBox.boxIcon,
    slHint: S.ordSlOn ? 'Draft on chart' : 'Required by Risk Rules',
    tpHint: S.ordTpOn ? 'Draft on chart' : 'Optional',
    slDist: slValid ? Math.abs(px - sl).toFixed(2) + ' pts' : '—',
    tpDist: tpValid ? Math.abs(tp - px).toFixed(2) + ' pts' : '—',
    slLoss: riskUsd ? '−' + c.fmtP(riskUsd) : '—',
    tpGain: rewardUsd ? '+' + c.fmtP(rewardUsd) : '—',
    // The draft stop and target, handed to the chart as attributes. The hand-drawn version positioned them itself
    // (`draftSlTop` was a percentage down a div); the widget takes the price and puts the order line where the price
    // actually is, which is the whole reason it is worth mounting.
    // ── Levels on the chart ───────────────────────────────────────────────────────────────────────────────────
    // Drawn from the number the trader is *typing*, not from the number that has passed validation. Waiting for
    // validity means the line appears only once the stop is already in the right place — which is precisely when
    // they no longer need to be shown where it is. An out-of-place level is drawn and marked invalid instead, so
    // "my stop is above my entry on a buy" is something you can see rather than only read in an error.
    // The chip above the chart. The package wrote XAUUSD as literal text; it now names what is loaded.
    deskSym,
    deskSymTitle: 'Change instrument — ' + deskSym + ' loaded',

    tvSl: S.ordSlOn && sl > 0 ? String(sl) : '',
    tvTp: S.ordTpOn && tp > 0 ? String(tp) : '',
    tvSlState: !S.ordSlOn || !(sl > 0) ? '' : slValid ? 'draft' : 'invalid',
    tvTpState: !S.ordTpOn || !(tp > 0) ? '' : tpValid ? 'draft' : 'invalid',

    // The committed levels — the stop and target of a position that actually exists, on the charted instrument.
    // Drawn solid where the draft is dashed, because a level that is working and a level being considered are
    // different things and a trader glancing at the chart has to be able to tell them apart.
    tvSlLive: liveSlPrice,
    tvTpLive: livePos && livePos.tp !== '—' ? livePos.tp : '',
    // A trailing stop moves with the price, so the marker moves with it. A static line labelled "trailing" would be
    // telling the trader something the chart is contradicting.
    tvSlTrailing: trailing ? '1' : '',
    tvEntry: livePos ? livePos.open : '',
    tvSide: livePos ? livePos.type : S.ordSide === 'buy' ? 'BUY' : 'SELL',
    ordErr: S.ordErr,
    ordHasErr: !!S.ordErr,
    ordBtnOp: err ? '0.55' : '1',
    ordBtnIcon: isBuy ? 'ph-trend-up' : 'ph-trend-down',
    ordBtnLabel:
      (isBuy ? 'BUY ' : 'SELL ') + (lots > 0 ? lots.toFixed(2) : S.ordLots || '0') + ' @ ' + px.toFixed(2),
    ordBtnBd: isBuy
      ? 'color-mix(in srgb, var(--pos) 70%, transparent)'
      : 'color-mix(in srgb, var(--neg) 70%, transparent)',
    ordBtnBg: isBuy
      ? 'color-mix(in srgb, var(--pos) 26%, transparent)'
      : 'color-mix(in srgb, var(--neg-fill) 30%, transparent)',
    ordBtnFg: isBuy ? 'var(--pos)' : 'var(--neg)',
    buyBd: isBuy ? 'color-mix(in srgb, var(--pos) 65%, transparent)' : 'var(--l8)',
    buyBg: isBuy ? 'color-mix(in srgb, var(--pos) 20%, transparent)' : 'transparent',
    buyFg: isBuy ? 'var(--pos)' : 'var(--muted)',
    sellBd: !isBuy ? 'color-mix(in srgb, var(--neg) 65%, transparent)' : 'var(--l8)',
    sellBg: !isBuy ? 'color-mix(in srgb, var(--neg-fill) 24%, transparent)' : 'transparent',
    sellFg: !isBuy ? 'var(--neg)' : 'var(--muted)',
    ordRisk: riskUsd ? '−' + c.fmtP(riskUsd) : '—',
    ordRiskFg: riskUsd ? 'var(--neg)' : 'var(--faint)',
    ordRiskPct: riskUsd ? pctOf(riskUsd) : 'Set lots + SL',
    ordReward: rewardUsd ? '+' + c.fmtP(rewardUsd) : '—',
    ordRewardFg: rewardUsd ? 'var(--pos)' : 'var(--faint)',
    ordRewardPct: rewardUsd ? pctOf(rewardUsd) : 'No take profit set',
    ordRr: rrTxt,
    ordRrNote:
      riskUsd && rewardUsd
        ? rewardUsd / riskUsd >= 2
          ? 'Meets your 1:2 target'
          : 'Below your 1:2 target'
        : 'Needs SL + TP',
    ordRiskNote: sized
      ? slValid
        ? 'Calculated from ' +
          lots.toFixed(2) +
          ' lots × ' +
          Math.abs(px - sl).toFixed(2) +
          ' pts × $' +
          CONTRACT +
          '/pt against ' +
          c.fmtP(EQUITY) +
          ' equity.'
        : 'Add a stop loss — risk cannot be calculated without one.'
      : 'Enter a lot size to calculate risk.',
    ordSubmit: submit,
    ordModalOpen: S.ordModal,
    ordCancel: () => c.set({ ordModal: false, ordDraft: null }),
    // §8: the header chip routes to the ONE authoritative instrument search — the side panel's. A collapsed
    // panel opens first; focus waits a tick for it to render. (Same document-touching precedent as core's menus.)
    thInstrumentGo: () => {
      if (!S.sideOpen) c.set({ sideOpen: true });
      setTimeout(() => {
        const el = document.querySelector<HTMLInputElement>('input[aria-label="Search symbols"]');
        if (el) {
          el.focus();
          el.select();
        }
      }, 60);
    },
    // §9: the add-symbol popover
    deskAddToggle: () => c.set((st) => ({ deskAddOpen: !st.deskAddOpen, deskAddInput: '' })),
    deskAddOpen: S.deskAddOpen,
    deskAddInput: S.deskAddInput,
    deskAddSet: (v: string) => c.set({ deskAddInput: v }),
    deskAddGo,
    ordConfirm: confirmOrder,
    // Every modal field reads THE DRAFT (mandate §5) — never the live desk, which may have retargeted underneath.
    ordPx: S.ordDraft ? S.ordDraft.px.toFixed(instrumentScale(S.ordDraft.sym).decimals) : '—',
    ordSym: S.ordDraft?.sym ?? deskSym,
    ordAccountLabel: S.ordDraft?.account ?? 'Session default',
    ordSideLabel: (S.ordDraft?.side ?? S.ordSide) === 'buy' ? 'BUY' : 'SELL',
    ordSlLabel: S.ordDraft
      ? S.ordDraft.sl === null
        ? 'Not set'
        : String(S.ordDraft.sl)
      : slStr || 'Not set',
    ordTpLabel: S.ordDraft
      ? S.ordDraft.tp === null
        ? 'Not set'
        : String(S.ordDraft.tp)
      : tpStr || 'Not set',

    // position management (mandate §2) — actions exposed for the row buttons and the composed overlays
    posBusy: S.posBusy,
    posCloseGo,
    posCloseCancel: () => c.set({ posCloseConfirm: null }),
    posCloseConfirm: S.posCloseConfirm,
    posCloseAllAsk,
    posCloseAllGo,
    lockDayAsk: () => c.set({ lockDay: { closeAll: false, busy: false } }),
    lockDayOpen: S.lockDay,
    lockDayToggleCloseAll: () =>
      c.set((st) => (st.lockDay ? { lockDay: { ...st.lockDay, closeAll: !st.lockDay.closeAll } } : {})),
    lockDayCancel: () => (S.lockDay?.busy ? undefined : c.set({ lockDay: null })),
    lockDayGo,
    lockDayOpenCount: S.openPos.length,
    dayLockedUntil: S.dayLockedUntil,
    posCloseAllCancel: () => c.set({ posCloseAllConfirm: false }),
    posCloseAllConfirmOpen: S.posCloseAllConfirm,
    posCloseAllCount: S.openPos.length,
    posModifyGo,
    posModifyCancel: () => c.set({ posModify: null }),
    posModifyDraft: S.posModify,
    posModifySetSl: (v: string) =>
      c.set((st) => (st.posModify ? { posModify: { ...st.posModify, sl: v } } : {})),
    posModifySetTp: (v: string) =>
      c.set((st) => (st.posModify ? { posModify: { ...st.posModify, tp: v } } : {})),

    // toast
    toast: S.toast,
    // Animated in and out rather than mounted and unmounted, so the exit is visible. Out is faster than in (150ms
    // against 220ms): a notice you have finished reading should get out of the way.
    toastOp: S.toast ? '1' : '0',
    toastY: S.toast ? '0px' : '10px',
    toastDur: S.toast ? '220ms' : '150ms',
    toastFg: S.toastKind === 'neg' ? 'var(--neg)' : S.toastKind === 'mut' ? 'var(--text-mid)' : 'var(--pos)',
    toastIcon:
      S.toastKind === 'neg' ? 'ph-warning-circle' : S.toastKind === 'mut' ? 'ph-info' : 'ph-check-circle',
    closeMenus: () => {
      if (S.menu) c.set({ menu: null });
    },
  } satisfies Partial<ViewModel>;
}
export type ExecModel = ReturnType<typeof buildExec>;
